import { useCallback, useMemo } from 'react'

import { format, formatISO } from 'date-fns'
import { useUnit } from 'effector-react'
import { FormProvider, useForm, useWatch } from 'react-hook-form'

import { Button } from '@/components/ui/button.tsx'
import { Dialog } from '@/components/ui/dialog.tsx'
import { FormField } from '@/components/ui/form/form-field'
import {
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select.tsx'
import { Separator } from '@/components/ui/separator.tsx'
import { $activeChatDetails } from '@/models/chattingUI/chats/chats.ts'
import { extractFanName } from '@/modules/chatting/ui/utils.ts'

import { DialogProps } from '../types'
import { MUTE_CHAT_PERIODS } from './constants'
import { getMuteUntil } from './utils'

interface Props extends DialogProps {
  onMute: (muteUntil: string) => Promise<void>
}

interface FormValues {
  days: number
  weeks: number
  hours: number
  muteFor: string
}

export const MuteChatDialog = ({ isOpen, onClose, onMute }: Props) => {
  const activeChat = useUnit($activeChatDetails)
  const form = useForm<FormValues>({
    defaultValues: {
      muteFor: 'always',
      weeks: 0,
      days: 0,
      hours: 0,
    },
  })

  const [muteFor, weeks, days, hours] = useWatch({
    name: ['muteFor', 'weeks', 'days', 'hours'],
    control: form.control,
  })

  const muteUntil = useMemo(
    () => getMuteUntil(muteFor, days, hours, weeks),
    [muteFor, days, hours, weeks],
  )

  const handleSubmit = useCallback(async () => {
    await onMute(muteUntil ? formatISO(muteUntil) : '')
    onClose()
  }, [onMute, muteUntil, onClose])

  const isSaveDisabled = muteFor === 'custom' && !weeks && !days && !hours

  const fanName = activeChat?.withUser
    ? extractFanName(activeChat.withUser)
    : 'Unknown'

  return (
    <Dialog
      show={isOpen}
      onClose={onClose}
      afterLeave={form.reset}
    >
      <Dialog.Header>
        <Dialog.Title>Mute {fanName}</Dialog.Title>
      </Dialog.Header>

      <FormProvider {...form}>
        <form onSubmit={form.handleSubmit(handleSubmit)}>
          <div className="mt-5">
            <div>
              <label className="text-text-default mb-1 block text-sm font-medium">
                Mute Duration
              </label>
              <Select
                onValueChange={(value) => form.setValue('muteFor', value)}
                value={muteFor}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectGroup>
                    <SelectItem value="always">Always</SelectItem>
                    <SelectItem value="8hrs">8 Hours</SelectItem>
                    <SelectItem value="1day">1 Day</SelectItem>
                    <SelectItem value="1week">1 Week</SelectItem>
                    <SelectItem value="custom">Custom Duration</SelectItem>
                  </SelectGroup>
                </SelectContent>
              </Select>
            </div>

            {muteFor === 'custom' && (
              <div className="mt-6">
                <label className="text-text-default mb-1 block text-sm font-medium">
                  Custom Mute
                </label>
                <div className="flex gap-4">
                  {MUTE_CHAT_PERIODS.map((item) => (
                    <FormField
                      key={item.name}
                      name={item.name}
                      type="number"
                      min={0}
                      step={1}
                      className="flex-1"
                      suffix={
                        <div className="flex items-center">
                          <Separator
                            orientation="vertical"
                            className="mr-2 h-5"
                          />
                          <span className="text-secondary-300">
                            {item.label}
                          </span>
                        </div>
                      }
                    />
                  ))}
                </div>
              </div>
            )}
            {muteUntil && (
              <p className="text-secondary-300 mt-1 text-xs">
                Mute until {format(muteUntil, 'MMMM dd, yyyy')} •{' '}
                {format(muteUntil, 'hh:mm a')}
              </p>
            )}
          </div>

          <Dialog.Footer className="flex gap-2">
            <Button
              className="flex-1"
              onClick={onClose}
              variant="outline"
              type="button"
              disabled={form.formState.isSubmitting}
            >
              Cancel
            </Button>
            <Button
              type="submit"
              className="flex-1"
              isLoading={form.formState.isSubmitting}
              disabled={isSaveDisabled}
            >
              Mute
            </Button>
          </Dialog.Footer>
        </form>
      </FormProvider>
    </Dialog>
  )
}
